import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Building2, MapPinned, ShieldCheck, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * HeaderSearch — a compact input in AppHeader that live-searches Clubs +
 * Facilities simultaneously (`GET /api/search?q=...`). Results are grouped
 * into two sections; clicking a result navigates to the detail page.
 * Pressing Enter navigates to `/clubs?q=…` so users can see the full list.
 */
export default function HeaderSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ clubs: [], facilities: [] });
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  const runSearch = useCallback(async (query) => {
    if (query.trim().length < 2) { setData({ clubs: [], facilities: [] }); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/search?q=${encodeURIComponent(query.trim())}&limit=6`);
      setData(d || { clubs: [], facilities: [] });
    } catch { setData({ clubs: [], facilities: [] }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 200);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const flat = [
    ...data.clubs.map((c) => ({ kind: "club", to: `/clubs/${c.slug || c.id}`, id: c.id, name: c.name, city: c.city, country: c.country, verified: !!c.is_verified, logo_url: c.logo_url })),
    ...data.facilities.map((f) => ({ kind: "facility", to: `/facilities/${f.slug || f.id}`, id: f.id, name: f.name, city: f.city, country: f.country, verified: !!f.verified })),
  ];

  const go = (item) => {
    setOpen(false);
    setQ("");
    navigate(item.to);
  };

  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active]) go(flat[active]);
      else if (q.trim()) { setOpen(false); navigate(`/clubs?q=${encodeURIComponent(q.trim())}`); setQ(""); }
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="header-search">
      <div className="relative">
        <Search size={14} className="absolute start-3 top-2.5 text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search clubs, venues…"
          data-testid="header-search-input"
          className="pp-input ps-9 pe-8 h-9 w-56 sm:w-64 text-sm"
        />
        {q && (
          <button
            onClick={() => { setQ(""); inputRef.current?.focus(); }}
            data-testid="header-search-clear"
            className="absolute end-2 top-2 text-zinc-500 hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute end-0 mt-1 w-80 bg-[#131313] border border-white/10 rounded-xl shadow-xl z-40 max-h-96 overflow-auto"
          data-testid="header-search-results"
        >
          {loading && <div className="text-zinc-500 text-xs p-3">Searching…</div>}
          {!loading && flat.length === 0 && (
            <div className="text-zinc-500 text-xs p-3" data-testid="header-search-empty">
              No matches for “{q.trim()}”.
            </div>
          )}

          {data.clubs.length > 0 && (
            <div className="p-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1">Clubs</div>
              {data.clubs.map((c, i) => (
                <button
                  key={`c-${c.id}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go({ to: `/clubs/${c.slug || c.id}` })}
                  data-testid={`header-search-club-${c.id}`}
                  className={`w-full text-start p-2 rounded-lg flex items-center gap-2 ${active === i ? "bg-white/5" : "hover:bg-white/5"}`}
                >
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="h-7 w-7 rounded-md object-cover border border-white/5 shrink-0" />
                  ) : (
                    <div className="h-7 w-7 rounded-md bg-[#00E676]/10 flex items-center justify-center shrink-0"><Building2 size={12} className="text-[#00E676]" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate flex items-center gap-1">
                      {c.name}
                      {c.is_verified && <ShieldCheck size={11} className="text-[#00E676] shrink-0" />}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{[c.city, c.country].filter(Boolean).join(", ")}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {data.facilities.length > 0 && (
            <div className="p-1 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-1">Venues</div>
              {data.facilities.map((f, i) => {
                const flatIdx = data.clubs.length + i;
                return (
                  <button
                    key={`f-${f.id}`}
                    onMouseEnter={() => setActive(flatIdx)}
                    onClick={() => go({ to: `/facilities/${f.slug || f.id}` })}
                    data-testid={`header-search-facility-${f.id}`}
                    className={`w-full text-start p-2 rounded-lg flex items-center gap-2 ${active === flatIdx ? "bg-white/5" : "hover:bg-white/5"}`}
                  >
                    <div className="h-7 w-7 rounded-md bg-[#007AFF]/10 flex items-center justify-center shrink-0"><MapPinned size={12} className="text-[#007AFF]" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate flex items-center gap-1">
                        {f.name}
                        {f.verified && <ShieldCheck size={11} className="text-[#00E676] shrink-0" />}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">{[f.city, f.country].filter(Boolean).join(", ")}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {flat.length > 0 && (
            <Link
              to={`/clubs?q=${encodeURIComponent(q.trim())}`}
              onClick={() => { setOpen(false); setQ(""); }}
              data-testid="header-search-see-all"
              className="block px-3 py-2 text-[11px] text-zinc-400 hover:text-[#00E676] border-t border-white/5"
            >
              See all results for “{q.trim()}” →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
